import React from 'react';
import {Button, UncontrolledTooltip} from 'reactstrap';
import API from "@beardeddevops/react.api";
import Framework from "@beardeddevops/react.framework";
import Types from "constants/Types";
import ObjectOverride from "../ObjectOverride";
import MrpLocationControl from "components/Parts/MrpLocationControl";
import {applyMrpLocationToRequest, getMrpLocationSignature, syncMrpActiveLocation} from "utils/mrpLocation";

const LOCATION_NOTE = 'ERP reads and writes follow the active location shown here. Changing it updates data across ERP screens.';

class MrpStation extends ObjectOverride {
	constructor(props) {
		super(props, Types.Mrp_Stations._name);
		this.state = {
			loading: false,
			loadingData: true,
			stations: [],
			showModal: false,
			editingStation: null,
			stationName: '',
			laborMinutes: 0,
			partMinutes: 0,
			error: '',
		// Parts management
		expandedStations: {}, // Track which stations have parts expanded
		stationParts: {}, // Map of station ID to parts array
		showPartModal: false,
		editingPart: null,
		partName: '',
		partDescription: '',
		partPrice: '',
		partQuantity: '',
		partSku: '',
		partUpc: '',
		partSoftAlert: '',
		partHardAlert: '',
		partOrderUrl: '',
		partCost: '',
		partXAcquired: '',
		partXLost: '',
		partXStolen: '',
		partXExpectedAcquired: '',
		partError: '',
		};
		this.modalOverlayMouseDownOnBackdrop = false;
	}

	componentDidMount() {
		syncMrpActiveLocation(this.props.rootState);
		this.loadStations().then();
	}

	componentDidUpdate(prevProps) {
		if (getMrpLocationSignature(prevProps?.rootState) !== getMrpLocationSignature(this.props?.rootState)) {
			this.loadStations().then();
		}
	}

	loadStations = async () => {
		this.setState({loadingData: true});
		try {
			let stationModel = new API.BuildClass(Types.Mrp_Stations._name, API.RequestType.READ_ALL);
			stationModel.parent = this.props.rootState.account_number;
			applyMrpLocationToRequest(stationModel, this.props.rootState);
			let data = await stationModel.submit(null, API.RequestType.READ_ALL);
			const stations = data.items || [];
			
			// Organize parts by station ID
			const stationParts = {};
			stations.forEach(station => {
				if (station.parts && Array.isArray(station.parts)) {
					stationParts[station.pkey || station.id] = station.parts;
				} else {
					stationParts[station.pkey || station.id] = [];
				}
			});
			
			this.setState({
				stations,
				stationParts
			});
		} catch (error) {
		} finally {
			this.setState({loadingData: false});
		}
	}

	loadPartsForStation = async (stationId) => {
		// Just toggle expansion since parts are already loaded
		this.setState(prevState => ({
			expandedStations: {
				...prevState.expandedStations,
				[stationId]: !prevState.expandedStations[stationId]
			}
		}));
	}

	normalizeOrderUrl = (urlValue) => {
		const trimmed = `${urlValue || ''}`.trim();
		if (!trimmed) {
			return '';
		}
		if (/^[a-zA-Z][a-zA-Z\d+.-]*:\/\//.test(trimmed)) {
			return trimmed;
		}
		return `https://${trimmed}`;
	}

	isValidOrderUrl = (urlValue) => {
		if (!urlValue) {
			return true;
		}
		try {
			const parsed = new URL(urlValue);
			return parsed.protocol === 'http:' || parsed.protocol === 'https:';
		} catch (e) {
			return false;
		}
	}

	getSoftAlert = (part) => Number(part.soft_alert ?? part.alert_quantity) || 0

	getHardAlert = (part) => Number(part.hard_alert) || 0

	getPartStockState = (part) => {
		const quantity = Number(part?.quantity) || 0;
		const softAlert = this.getSoftAlert(part);
		const hardAlert = this.getHardAlert(part);
		const isHardLow = hardAlert > 0 && quantity <= hardAlert;
		const isLow = !isHardLow && softAlert > 0 && quantity < softAlert;
		return {
			quantity,
			softAlert,
			hardAlert,
			isHardLow,
			isLow,
			isLowStock: isHardLow || isLow,
		};
	}

	isBackdropPointerEvent = (event) => Boolean(event && event.target === event.currentTarget)

	handleModalOverlayMouseDown = (event) => {
		this.modalOverlayMouseDownOnBackdrop = this.isBackdropPointerEvent(event);
	}

	handleModalOverlayClick = (closeHandler) => (event) => {
		const shouldClose = this.modalOverlayMouseDownOnBackdrop && this.isBackdropPointerEvent(event);
		this.modalOverlayMouseDownOnBackdrop = false;
		if (shouldClose && typeof closeHandler === 'function') {
			closeHandler();
		}
	}

	handleShowPartModal = (station, part = null) => {
		if (part) {
			this.setState({
				showPartModal: true,
				editingPart: part,
				editingStation: station,
				partName: part.name || '',
				partDescription: part.description || '',
				partPrice: part.price || '',
				partQuantity: part.quantity || '',
				partSku: part.sku || '',
				partUpc: part.upc || '',
				partSoftAlert: part.soft_alert ?? part.alert_quantity ?? '',
				partHardAlert: part.hard_alert ?? '',
				partOrderUrl: part.order_url || '',
				partCost: part.cost || '',
				partXAcquired: part.x_acquired || '',
				partXLost: part.x_lost || '',
				partXStolen: part.x_stolen || '',
				partXExpectedAcquired: part.x_expected_acquired || '',
				partError: ''
			});
		} else {
			this.setState({
				showPartModal: true,
				editingPart: null,
				editingStation: station,
				partName: '',
				partDescription: '',
				partPrice: '',
				partQuantity: '',
				partSku: '',
				partUpc: '',
				partSoftAlert: '',
				partHardAlert: '',
				partOrderUrl: '',
				partCost: '',
				partXAcquired: '',
				partXLost: '',
				partXStolen: '',
				partXExpectedAcquired: '',
				partError: ''
			});
		}
	}

	handleClosePartModal = () => {
		this.modalOverlayMouseDownOnBackdrop = false;
		this.setState({
			showPartModal: false,
			editingPart: null,
			editingStation: null,
			partName: '',
			partDescription: '',
			partPrice: '',
			partQuantity: '',
			partSku: '',
			partUpc: '',
			partSoftAlert: '',
			partHardAlert: '',
			partOrderUrl: '',
			partCost: '',
			partXAcquired: '',
			partXLost: '',
			partXStolen: '',
			partXExpectedAcquired: '',
			partError: ''
		});
	}

	handleSavePart = async () => {
		if (this.state.loading) {
			return;
		}

		if (!this.state.partName.trim()) {
			this.setState({partError: 'Item name is required'});
			return;
		}

		this.setState({loading: true, partError: ''});
		try {
			if (!this.state.editingStation) {
				this.setState({partError: 'Station is required', loading: false});
				return;
			}

			const stationId = this.state.editingStation.pkey || this.state.editingStation.id;
			if (!stationId) {
				this.setState({partError: 'Invalid station', loading: false});
				return;
			}

			const normalizedOrderUrl = this.normalizeOrderUrl(this.state.partOrderUrl);
			if (!this.isValidOrderUrl(normalizedOrderUrl)) {
				this.setState({partError: 'Order URL must be a valid http(s) URL', loading: false});
				return;
			}

			const softAlertValue = Number.parseInt(this.state.partSoftAlert, 10);
			const hardAlertValue = Number.parseInt(this.state.partHardAlert, 10);

		if (this.state.editingPart) {
			// Update part
			let partModel = new API.BuildClass(Types.Mrp_Parts._name, API.RequestType.UPDATE);
			partModel.properties.sku = {settable: true};
			partModel.properties.upc = {settable: true};
			partModel.object.pkey = this.state.editingPart.pkey;
			partModel.object.name = this.state.partName.trim();
			partModel.object.description = this.state.partDescription.trim();
			partModel.object.price = this.state.partPrice ? parseFloat(this.state.partPrice) : 0;
			partModel.object.quantity = this.state.partQuantity ? this.state.partQuantity.trim() : '';
			partModel.object.sku = this.state.partSku ? this.state.partSku.trim() : '';
			partModel.object.upc = this.state.partUpc ? this.state.partUpc.trim() : '';
			partModel.object.soft_alert = Number.isNaN(softAlertValue) ? 0 : softAlertValue;
			partModel.object.hard_alert = Number.isNaN(hardAlertValue) ? 0 : hardAlertValue;
			partModel.object.order_url = normalizedOrderUrl;
			partModel.object.cost = this.state.partCost ? parseFloat(this.state.partCost) : 0;
			partModel.object.x_acquired = this.state.partXAcquired ? this.state.partXAcquired.trim() : '';
			partModel.object.x_lost = this.state.partXLost ? this.state.partXLost.trim() : '';
			partModel.object.x_stolen = this.state.partXStolen ? this.state.partXStolen.trim() : '';
			partModel.object.x_expected_acquired = this.state.partXExpectedAcquired ? this.state.partXExpectedAcquired.trim() : '';
			partModel.object.station = stationId;
			applyMrpLocationToRequest(partModel, this.props.rootState);
			partModel.parent = this.state.editingPart.pkey;
			await partModel.submit(null, API.RequestType.UPDATE, true);
			API.Toast.success('Item updated successfully');
		} else {
			// Create part
			let partModel = new API.BuildClass(Types.Mrp_Parts._name, API.RequestType.CREATE);
			partModel.properties.sku = {settable: true};
			partModel.properties.upc = {settable: true};
			partModel.object.name = this.state.partName.trim();
			partModel.object.description = this.state.partDescription.trim();
			partModel.object.price = this.state.partPrice ? parseFloat(this.state.partPrice) : 0;
			partModel.object.quantity = this.state.partQuantity ? this.state.partQuantity.trim() : '';
			partModel.object.sku = this.state.partSku ? this.state.partSku.trim() : '';
			partModel.object.upc = this.state.partUpc ? this.state.partUpc.trim() : '';
			partModel.object.soft_alert = Number.isNaN(softAlertValue) ? 0 : softAlertValue;
			partModel.object.hard_alert = Number.isNaN(hardAlertValue) ? 0 : hardAlertValue;
			partModel.object.order_url = normalizedOrderUrl;
			partModel.object.cost = this.state.partCost ? parseFloat(this.state.partCost) : 0;
			partModel.object.x_acquired = this.state.partXAcquired ? this.state.partXAcquired.trim() : '';
			partModel.object.x_lost = this.state.partXLost ? this.state.partXLost.trim() : '';
			partModel.object.x_stolen = this.state.partXStolen ? this.state.partXStolen.trim() : '';
			partModel.object.x_expected_acquired = this.state.partXExpectedAcquired ? this.state.partXExpectedAcquired.trim() : '';
			partModel.object.station = stationId;
			applyMrpLocationToRequest(partModel, this.props.rootState);
			partModel.parent = this.props.rootState.account_number;
			const result = await partModel.submit(null, API.RequestType.CREATE, true);
			API.Toast.success('Item created successfully');
		}

			// Reload stations (which includes parts)
			const editingStationId = this.state.editingStation.pkey || this.state.editingStation.id;
			await this.loadStations();
			
			// Expand the station that was just edited
			this.setState(prevState => ({
				expandedStations: {
					...prevState.expandedStations,
					[editingStationId]: true
				}
			}));

			this.handleClosePartModal();
		} catch (error) {
			API.Toast.error(this.state.editingPart ? 'Failed to update item' : 'Failed to create item');
			console.error('Error saving part:', error);
		} finally {
			this.setState({loading: false});
		}
	}

	handleDeletePart = async (station, part) => {
		if (!window.confirm(`Are you sure you want to delete "${part.name}"? This action cannot be undone.`)) {
			return;
		}

		this.setState({loading: true});
		try {
			let partModel = new API.BuildClass(Types.Mrp_Parts._name, API.RequestType.DELETE);
			partModel.object.pkey = part.pkey;
			applyMrpLocationToRequest(partModel, this.props.rootState);
			partModel.parent = part.pkey;
			await partModel.submit(null, API.RequestType.DELETE);
			
			// Reload stations (which includes parts)
			await this.loadStations();
			
			API.Toast.success('Item deleted successfully');
		} catch (error) {
			API.Toast.error('Failed to delete item');
			console.error('Error deleting part:', error);
		} finally {
			this.setState({loading: false});
		}
	}

	handleShowModal = (station = null) => {
		if (station) {
			this.setState({
				showModal: true,
				editingStation: station,
				stationName: station.name,
				laborMinutes: station.laborMinutes || 0,
				partMinutes: station.partMinutes || 0,
				error: ''
			});
		} else {
			this.setState({
				showModal: true,
				editingStation: null,
				stationName: '',
				laborMinutes: 0,
				partMinutes: 0,
				error: ''
			});
		}
	}

	handleCloseModal = () => {
		this.modalOverlayMouseDownOnBackdrop = false;
		this.setState({
			showModal: false,
			editingStation: null,
			stationName: '',
			laborMinutes: 0,
			partMinutes: 0,
			error: ''
		});
	}

	handleSave = async () => {
		if (this.state.loading) {
			return; // Prevent double submission
		}

		if (!this.state.stationName.trim()) {
			this.setState({error: 'Station name is required'});
			return;
		}

		this.setState({loading: true, error: ''});
		try {
			const stationName = this.state.stationName.trim();
			if (this.state.editingStation) {
				const stationId = this.state.editingStation.pkey || this.state.editingStation.id;
				let stationModel = new API.BuildClass(Types.Mrp_Stations._name, API.RequestType.UPDATE);
				stationModel.object.pkey = stationId;
				stationModel.object.name = stationName;
				applyMrpLocationToRequest(stationModel, this.props.rootState);
				stationModel.parent = stationId;
				await stationModel.submit(null, API.RequestType.UPDATE, true);
				await this.loadStations();
				API.Toast.success('Station updated successfully');
			} else {
				let stationModel = new API.BuildClass(Types.Mrp_Stations._name, API.RequestType.CREATE);
				stationModel.object.name = stationName;
				applyMrpLocationToRequest(stationModel, this.props.rootState);
				stationModel.parent = this.props.rootState.account_number;
				await stationModel.submit(null, API.RequestType.CREATE);
				await this.loadStations();
				API.Toast.success('Station created successfully');
			}
			this.handleCloseModal();
		} catch (error) {
			API.Toast.error(this.state.editingStation ? 'Failed to update station' : 'Failed to create station');
			console.error('Error saving station:', error);
		} finally {
			this.setState({loading: false});
		}
	}

	handleDelete = async (station) => {
		if (!window.confirm(`Are you sure you want to delete "${station.name}"? This action cannot be undone.`)) {
			return;
		}

		this.setState({loading: true});
		try {
			const stationId = station.pkey || station.id;
			let stationModel = new API.BuildClass(Types.Mrp_Stations._name, API.RequestType.DELETE);
			stationModel.object.pkey = stationId;
			applyMrpLocationToRequest(stationModel, this.props.rootState);
			stationModel.parent = stationId;
			await stationModel.submit(null, API.RequestType.DELETE);
			await this.loadStations();
			API.Toast.success('Station deleted successfully');
		} catch (error) {
			API.Toast.error('Failed to delete station');
			console.error('Error deleting station:', error);
		} finally {
			this.setState({loading: false});
		}
	}

	render() {
		if (this.state.loadingData) {
			return <Framework.Elements.LoadingSpinner isLoading={true}/>;
		}
		const totalStations = this.state.stations.length;
		const totalItems = Object.values(this.state.stationParts || {}).reduce(
			(sum, parts) => sum + (Array.isArray(parts) ? parts.length : 0),
			0
		);
		const lowStockItems = Object.values(this.state.stationParts || {}).reduce((sum, parts) => {
			const items = Array.isArray(parts) ? parts : [];
			return sum + items.filter((part) => this.getPartStockState(part).isLowStock).length;
		}, 0);

		return (
			<div className="row">
				<div className="col-12">
					<div style={stationPageStyles.heroBand} className="mb-3">
						<div className="row align-items-center">
							<div className="col-lg-8">
								<div style={stationPageStyles.heroEyebrow}>ERP Stations</div>
								<h2 style={stationPageStyles.heroTitle}>Configure Stations and Job Inputs</h2>
								<div style={stationPageStyles.heroSubtext}>
									Maintain station order, station item lists, and required quantities so ERP jobs route correctly and shortages are visible early.
								</div>
							</div>
							<div className="col-lg-4 mt-3 mt-lg-0">
								<div style={stationPageStyles.heroStatsWrap}>
									<div style={stationPageStyles.heroStatRow}><span style={stationPageStyles.heroStatLabel}>Stations</span><strong style={stationPageStyles.heroStatValue}>{totalStations}</strong></div>
									<div style={stationPageStyles.heroStatRow}><span style={stationPageStyles.heroStatLabel}>Items</span><strong style={stationPageStyles.heroStatValue}>{totalItems}</strong></div>
									<div style={stationPageStyles.heroStatRow}><span style={stationPageStyles.heroStatLabel}>Low Stock</span><strong style={stationPageStyles.heroStatValue}>{lowStockItems}</strong></div>
									<div className="mt-2">
											<MrpLocationControl
												rootState={this.props.rootState}
												id="mrp-station-location"
												selectStyle={stationPageStyles.heroSelect}
												note={LOCATION_NOTE}
											/>
										</div>
									</div>
							</div>
						</div>
					</div>
					<div className="card" style={stationPageStyles.cardShell}>
						<div className="card-header d-flex justify-content-between align-items-center" style={stationPageStyles.cardHeader}>
							<strong>Stations</strong>
							<div style={{display: "flex", gap: "8px", flexWrap: 'wrap'}}>
								<Button
									color="secondary"
									size="sm"
									style={stationPageStyles.secondaryAction}
									onClick={() => this.props.history.push('/erp/jobs')}
								>
									Back to Jobs
								</Button>
								<Button
									color="primary"
									size="sm"
									style={stationPageStyles.primaryAction}
									onClick={() => this.handleShowModal()}
									disabled={this.state.loading}
								>
									<i className="fa fa-plus"/> Add Station
								</Button>
							</div>
						</div>
						<div className="card-body p-0">
							{this.state.stations.length === 0 ? (
								<div className="text-center p-4 text-muted">
									No stations found. Click "Add Station" to create your first station.
								</div>
							) : (
								<div className="table-responsive">
									<table className="table table-striped table-hover mb-0" style={stationPageStyles.table}>
										<thead>
											<tr>
												<th style={{width: 80}}>Items</th>
												<th>Station Name</th>
												<th style={{width: 300}}>Actions</th>
											</tr>
										</thead>
										<tbody>
											{this.state.stations.map((station) => {
												const stationId = station.pkey || station.id;
												const isExpanded = this.state.expandedStations[stationId];
												const parts = this.state.stationParts[stationId] || [];
												const lowStockCount = parts.filter((part) => this.getPartStockState(part).isLowStock).length;
												const qohTooltipId = `mrp-station-qoh-${stationId}`;
												return (
													<React.Fragment key={stationId}>
														<tr>
															<td>
																<span className="badge badge-light" style={stationPageStyles.itemCountBadge}>
																	{parts.length}
																</span>
															</td>
															<td>
																<div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
																	<button
																		type="button"
																		className="btn btn-link p-0"
																		onClick={() => this.loadPartsForStation(stationId)}
																		style={stationPageStyles.expandToggle}
																	>
																		<i className={`fa fa-chevron-${isExpanded ? 'down' : 'right'}`} style={{marginRight: '5px'}}/>
																	</button>
																	{station.name}
																	{parts.length > 0 && (
																		<>
																			<span style={stationPageStyles.itemCountBadge}>
																				{parts.length} {parts.length === 1 ? 'item' : 'items'}
																			</span>
																			{lowStockCount > 0 && (
																				<span style={stationPageStyles.lowStockCountBadge}>
																					{lowStockCount} low
																				</span>
																			)}
																		</>
																	)}
																</div>
															</td>
															<td>
																<div style={{display: 'flex', gap: '8px', flexWrap: 'wrap'}}>
																	<Button
																		color="info"
																		size="sm"
																		style={stationPageStyles.infoAction}
																		onClick={() => this.handleShowPartModal(station)}
																		disabled={this.state.loading}
																		title="Create New Item"
																	>
																		<i className="fa fa-plus"/> New Item
																	</Button>
																	<Button
																		color="primary"
																		size="sm"
																		style={stationPageStyles.primaryAction}
																		onClick={() => this.handleShowModal(station)}
																		disabled={this.state.loading}
																	>
																		<i className="fa fa-pencil"/> Edit
																	</Button>
																	<Button
																		color="danger"
																		size="sm"
																		style={stationPageStyles.dangerAction}
																		onClick={() => this.handleDelete(station)}
																		disabled={this.state.loading}
																	>
																		<i className="fa fa-trash"/> Delete
																	</Button>
																</div>
															</td>
														</tr>
														{isExpanded && (
															<tr>
																<td colSpan="3" style={stationPageStyles.expandedCell}>
																	<div style={stationPageStyles.expandedBody}>
																		{parts.length === 0 ? (
																			<div className="text-center p-3 text-muted">
																				No items associated with this station. Click "+ New Item" to add one.
																			</div>
																		) : (
																			<div>
																				<h6 style={{marginBottom: '10px', fontWeight: '600'}}>Items ({parts.length})</h6>
																				<div className="table-responsive">
																					<table className="table table-sm table-bordered mb-0" style={stationPageStyles.partsTable}>
																						<thead>
																							<tr>
																								<th>Name</th>
																								<th>Description</th>
																								<th style={{width: 120}}>Reorder</th>
																								<th style={{width: 100}}>
																									<span id={qohTooltipId} style={{borderBottom: '1px dashed #999', cursor: 'help'}}>
																										QoH
																									</span>
																									<UncontrolledTooltip placement="top" target={qohTooltipId}>
																										Quantity on Hand
																									</UncontrolledTooltip>
																								</th>
																								<th style={{width: 110}}>Soft Alert</th>
																								<th style={{width: 110}}>Hard Alert</th>
																								<th style={{width: 120}}>Cost</th>
																								<th style={{width: 150}}>Actions</th>
																							</tr>
																						</thead>
																						<tbody>
																							{parts.map((part) => {
																								const {
																									quantity: quantityValue,
																									isHardLow,
																									isLow,
																								} = this.getPartStockState(part);
																								const normalizedOrderUrl = this.normalizeOrderUrl(part.order_url);
																								const hasOrderUrl = !!normalizedOrderUrl;
																								return (
																									<tr key={part.pkey} className={isHardLow ? 'table-danger' : (isLow ? 'table-warning' : undefined)}>
																										<td>{part.name}</td>
																										<td>{part.description || '-'}</td>
																										<td>
																											{hasOrderUrl ? (
																												<Button
																													tag="a"
																													href={normalizedOrderUrl}
																													target="_blank"
																													rel="noopener noreferrer"
																													color="secondary"
																													size="sm"
																													outline
																												>
																													Reorder
																												</Button>
																											) : (
																												<span className="text-muted">-</span>
																											)}
																										</td>
																										<td>
																											<span className={(isLow || isHardLow) ? 'text-danger font-weight-bold' : undefined}>
																												{part.quantity || '0'}
																											</span>
																											{(isLow || isHardLow) && (
																												<span className={`badge ${isHardLow ? 'badge-danger' : 'badge-warning'}`} style={{marginLeft: '6px'}}>
																													{isHardLow ? 'Critical' : 'Low'}
																												</span>
																											)}
																										</td>
																										<td>{part.soft_alert ?? part.alert_quantity ?? '0'}</td>
																										<td>{part.hard_alert || '0'}</td>
																										<td>${part.cost ? parseFloat(part.cost).toFixed(2) : '0.00'}</td>
																										<td>
																											<Button
																												color="primary"
																												size="sm"
																												onClick={() => this.handleShowPartModal(station, part)}
																												disabled={this.state.loading}
																												style={{marginRight: '5px'}}
																											>
																												<i className="fa fa-pencil"/> Edit
																											</Button>
																											<Button
																												color="danger"
																												size="sm"
																												onClick={() => this.handleDeletePart(station, part)}
																												disabled={this.state.loading}
																											>
																												<i className="fa fa-trash"/> Delete
																											</Button>
																										</td>
																									</tr>
																								);
																							})}
																						</tbody>
																					</table>
																				</div>
																			</div>
																		)}
																	</div>
																</td>
															</tr>
														)}
													</React.Fragment>
												);
											})}
										</tbody>
									</table>
								</div>
							)}
						</div>
					</div>
				</div>

				{/* Station Add/Edit Modal */}
				{this.state.showModal && (
					<div
						style={modalStyles.modalOverlay}
						onMouseDown={this.handleModalOverlayMouseDown}
						onClick={this.handleModalOverlayClick(this.handleCloseModal)}
					>
						<div 
							style={modalStyles.modalContent} 
							onClick={(e) => e.stopPropagation()}
							onKeyDown={(e) => {
								if (e.key === 'Enter' && !e.shiftKey && !this.state.loading) {
									e.preventDefault();
									this.handleSave();
								}
							}}
							tabIndex={-1}
						>
							<h2 style={modalStyles.heading}>
								{this.state.editingStation ? 'Edit Station' : 'Add New Station'}
							</h2>
							<div style={modalStyles.inputGroup}>
								<label htmlFor="stationName" style={modalStyles.label}>
									Station Name: <span className="text-danger">*</span>
								</label>
								<input
									id="stationName"
									type="text"
									value={this.state.stationName}
									onChange={(e) => this.setState({stationName: e.target.value, error: ''})}
									onKeyDown={(e) => {
										if (e.key === 'Enter' && !e.shiftKey && !this.state.loading) {
											e.preventDefault();
											e.stopPropagation();
											this.handleSave();
										}
									}}
									style={modalStyles.inputField}
									autoFocus
								/>
							</div>
							{/*<div style={modalStyles.inputGroup}>*/}
							{/*	<label htmlFor="laborMinutes" style={modalStyles.label}>*/}
							{/*		Labor Minutes:*/}
							{/*	</label>*/}
							{/*	<input*/}
							{/*		id="laborMinutes"*/}
							{/*		type="number"*/}
							{/*		min="0"*/}
							{/*		value={this.state.laborMinutes}*/}
							{/*		onChange={(e) => this.setState({laborMinutes: e.target.value})}*/}
							{/*		onKeyDown={(e) => {*/}
							{/*			if (e.key === 'Enter' && !e.shiftKey && !this.state.loading) {*/}
							{/*				e.preventDefault();*/}
							{/*				this.handleSave();*/}
							{/*			}*/}
							{/*		}}*/}
							{/*		style={modalStyles.inputField}*/}
							{/*	/>*/}
							{/*</div>*/}
							{/*<div style={modalStyles.inputGroup}>*/}
							{/*	<label htmlFor="partMinutes" style={modalStyles.label}>*/}
							{/*		Part Minutes:*/}
							{/*	</label>*/}
							{/*	<input*/}
							{/*		id="partMinutes"*/}
							{/*		type="number"*/}
							{/*		min="0"*/}
							{/*		value={this.state.partMinutes}*/}
							{/*		onChange={(e) => this.setState({partMinutes: e.target.value})}*/}
							{/*		onKeyDown={(e) => {*/}
							{/*			if (e.key === 'Enter' && !e.shiftKey && !this.state.loading) {*/}
							{/*				e.preventDefault();*/}
							{/*				this.handleSave();*/}
							{/*			}*/}
							{/*		}}*/}
							{/*		style={modalStyles.inputField}*/}
							{/*	/>*/}
							{/*</div>*/}
							{this.state.error && <p style={modalStyles.errorText}>{this.state.error}</p>}
							<div style={modalStyles.buttonGroup}>
								<button
									style={modalStyles.cancelButton}
									onClick={this.handleCloseModal}
									disabled={this.state.loading}
								>
									Cancel
								</button>
								<button
									style={modalStyles.saveButton}
									onClick={this.handleSave}
									disabled={this.state.loading}
								>
									{this.state.loading ? <i className="fa fa-spinner fa-spin"/> : null}
									{' '}Save
								</button>
							</div>
						</div>
					</div>
				)}

				{/* Part Add/Edit Modal */}
				{this.state.showPartModal && (
					<div
						style={modalStyles.modalOverlay}
						onMouseDown={this.handleModalOverlayMouseDown}
						onClick={this.handleModalOverlayClick(this.handleClosePartModal)}
					>
						<div
							style={modalStyles.modalContentWide}
							onClick={(e) => e.stopPropagation()}
							onKeyDown={(e) => {
								if (e.key === 'Enter' && !e.shiftKey && !this.state.loading) {
									e.preventDefault();
									this.handleSavePart();
								}
							}}
							tabIndex={-1}
						>
							<h2 style={modalStyles.heading}>
								{this.state.editingPart ? 'Edit Item' : 'Add New Item'}
								{this.state.editingStation && (
									<span style={{fontSize: '14px', fontWeight: 'normal', color: '#666', display: 'block', marginTop: '5px'}}>
										for {this.state.editingStation.name}
									</span>
								)}
							</h2>
							<div style={modalStyles.inputGroup}>
								<label htmlFor="partName" style={modalStyles.label}>
									Item Name: <span className="text-danger">*</span>
								</label>
								<input
									id="partName"
									type="text"
									value={this.state.partName}
									onChange={(e) => this.setState({partName: e.target.value, partError: ''})}
									onKeyDown={(e) => {
										if (e.key === 'Enter' && !e.shiftKey && !this.state.loading) {
											e.preventDefault();
											e.stopPropagation();
											this.handleSavePart();
										}
									}}
									style={modalStyles.inputField}
									autoFocus
								/>
							</div>
							<div style={modalStyles.inputGroup}>
								<label htmlFor="partDescription" style={modalStyles.label}>
									Description:
								</label>
								<textarea
									id="partDescription"
									value={this.state.partDescription}
									onChange={(e) => this.setState({partDescription: e.target.value, partError: ''})}
									style={{...modalStyles.inputField, minHeight: '80px', resize: 'vertical'}}
									rows={3}
								/>
							</div>
							<div style={modalStyles.inputGroup}>
								<label htmlFor="partCost" style={modalStyles.label}>
									Cost:
								</label>
								<input
									id="partCost"
									type="number"
									step="0.01"
									min="0"
									value={this.state.partCost}
									onChange={(e) => this.setState({partCost: e.target.value, partError: ''})}
									onKeyDown={(e) => {
										if (e.key === 'Enter' && !e.shiftKey && !this.state.loading) {
											e.preventDefault();
											e.stopPropagation();
											this.handleSavePart();
										}
									}}
									style={modalStyles.inputField}
									placeholder="0.00"
								/>

							</div>
							<div style={modalStyles.inputGroup}>
								<label htmlFor="partPrice" style={modalStyles.label}>
									Price:
								</label>
								<input
									id="partPrice"
									type="number"
									step="0.01"
									min="0"
									value={this.state.partPrice}
									onChange={(e) => this.setState({partPrice: e.target.value, partError: ''})}
									onKeyDown={(e) => {
										if (e.key === 'Enter' && !e.shiftKey && !this.state.loading) {
											e.preventDefault();
											e.stopPropagation();
											this.handleSavePart();
										}
									}}
									style={modalStyles.inputField}
									placeholder="0.00"
								/>
								<div className="small text-muted mt-2">
									Price is an optional reference amount. Cost is the unit cost used for ERP inventory value and scrap-cost
									reporting.
								</div>
							</div>
							<div style={modalStyles.inputGroup}>
								<label htmlFor="partQuantity" style={modalStyles.label}>
									Quantity:
								</label>
								<input
									id="partQuantity"
									type="text"
									// maxLength="2"
									value={this.state.partQuantity}
									onChange={(e) => this.setState({partQuantity: e.target.value, partError: ''})}
									onKeyDown={(e) => {
										if (e.key === 'Enter' && !e.shiftKey && !this.state.loading) {
											e.preventDefault();
											e.stopPropagation();
											this.handleSavePart();
										}
									}}
									style={modalStyles.inputField}
									placeholder="0"
								/>
								<div className="small text-muted mt-2">
									Current on-hand quantity for this item at the selected ERP location.
								</div>
							</div>
							<div style={modalStyles.inputGroup}>
								<label htmlFor="partSku" style={modalStyles.label}>
									SKU:
								</label>
								<input
									id="partSku"
									type="text"
									maxLength="255"
									value={this.state.partSku}
									onChange={(e) => this.setState({partSku: e.target.value, partError: ''})}
									onKeyDown={(e) => {
										if (e.key === 'Enter' && !e.shiftKey && !this.state.loading) {
											e.preventDefault();
											e.stopPropagation();
											this.handleSavePart();
										}
									}}
									style={modalStyles.inputField}
									placeholder=""
								/>
							</div>
							<div style={modalStyles.inputGroup}>
								<label htmlFor="partUpc" style={modalStyles.label}>
									UPC:
								</label>
								<input
									id="partUpc"
									type="text"
									maxLength="255"
									value={this.state.partUpc}
									onChange={(e) => this.setState({partUpc: e.target.value, partError: ''})}
									onKeyDown={(e) => {
										if (e.key === 'Enter' && !e.shiftKey && !this.state.loading) {
											e.preventDefault();
											e.stopPropagation();
											this.handleSavePart();
										}
									}}
									style={modalStyles.inputField}
									placeholder=""
								/>
							</div>
							<div style={modalStyles.inputGroup}>
								<label htmlFor="partSoftAlert" style={modalStyles.label}>
									Soft Alert:
								</label>
								<input
									id="partSoftAlert"
									type="number"
									min="0"
									value={this.state.partSoftAlert}
									onChange={(e) => this.setState({partSoftAlert: e.target.value, partError: ''})}
									onKeyDown={(e) => {
										if (e.key === 'Enter' && !e.shiftKey && !this.state.loading) {
											e.preventDefault();
											e.stopPropagation();
											this.handleSavePart();
										}
									}}
									style={modalStyles.inputField}
									placeholder="0"
								/>
							</div>
							<div style={modalStyles.inputGroup}>
								<label htmlFor="partHardAlert" style={modalStyles.label}>
									Hard Alert:
								</label>
								<input
									id="partHardAlert"
									type="number"
									min="0"
									value={this.state.partHardAlert}
									onChange={(e) => this.setState({partHardAlert: e.target.value, partError: ''})}
									onKeyDown={(e) => {
										if (e.key === 'Enter' && !e.shiftKey && !this.state.loading) {
											e.preventDefault();
											e.stopPropagation();
											this.handleSavePart();
										}
									}}
									style={modalStyles.inputField}
									placeholder="0"
								/>
								<div className="small text-muted mt-2">
									Soft Alert warns that stock is running low. Hard Alert marks the item as critical in ERP shortage views, and when quantity drops to or below this number, we will email your location's email address so your team can reorder quickly.
								</div>
							</div>
							<div style={modalStyles.inputGroup}>
								<label htmlFor="partOrderUrl" style={modalStyles.label}>
									Order URL:
								</label>
								<input
									id="partOrderUrl"
									type="url"
									value={this.state.partOrderUrl}
									onChange={(e) => this.setState({partOrderUrl: e.target.value, partError: ''})}
									onKeyDown={(e) => {
										if (e.key === 'Enter' && !e.shiftKey && !this.state.loading) {
											e.preventDefault();
											e.stopPropagation();
											this.handleSavePart();
										}
									}}
									style={modalStyles.inputField}
									placeholder="https://example.com/reorder"
								/>
								<div className="small text-muted mt-2">
									Optional reorder link used by the Reorder button in the station item table.
								</div>
							</div>

							<div style={modalStyles.inputGroup}>
								<label htmlFor="partXAcquired" style={modalStyles.label}>
									# Acquired:
								</label>
								<input
									id="partXAcquired"
									type="text"
									maxLength="255"
									value={this.state.partXAcquired}
									onChange={(e) => this.setState({partXAcquired: e.target.value, partError: ''})}
									onKeyDown={(e) => {
										if (e.key === 'Enter' && !e.shiftKey && !this.state.loading) {
											e.preventDefault();
											e.stopPropagation();
											this.handleSavePart();
										}
									}}
									style={modalStyles.inputField}
									placeholder=""
								/>
							</div>
							<div style={modalStyles.inputGroup}>
								<label htmlFor="partXLost" style={modalStyles.label}>
									# Lost:
								</label>
								<input
									id="partXLost"
									type="text"
									value={this.state.partXLost}
									onChange={(e) => this.setState({partXLost: e.target.value, partError: ''})}
									onKeyDown={(e) => {
										if (e.key === 'Enter' && !e.shiftKey && !this.state.loading) {
											e.preventDefault();
											e.stopPropagation();
											this.handleSavePart();
										}
									}}
									style={modalStyles.inputField}
									placeholder=""
								/>
							</div>
							<div style={modalStyles.inputGroup}>
								<label htmlFor="partXStolen" style={modalStyles.label}>
									# Stolen:
								</label>
								<input
									id="partXStolen"
									type="text"
									maxLength="25"
									value={this.state.partXStolen}
									onChange={(e) => this.setState({partXStolen: e.target.value, partError: ''})}
									onKeyDown={(e) => {
										if (e.key === 'Enter' && !e.shiftKey && !this.state.loading) {
											e.preventDefault();
											e.stopPropagation();
											this.handleSavePart();
										}
									}}
									style={modalStyles.inputField}
									placeholder=""
								/>
							</div>
							<div style={modalStyles.inputGroup}>
								<label htmlFor="partXExpectedAcquired" style={modalStyles.label}>
									# Expected Acquired:
								</label>
								<input
									id="partXExpectedAcquired"
									type="text"
									maxLength="255"
									value={this.state.partXExpectedAcquired}
									onChange={(e) => this.setState({partXExpectedAcquired: e.target.value, partError: ''})}
									onKeyDown={(e) => {
										if (e.key === 'Enter' && !e.shiftKey && !this.state.loading) {
											e.preventDefault();
											e.stopPropagation();
											this.handleSavePart();
										}
									}}
									style={modalStyles.inputField}
									placeholder=""
								/>
								<div className="small text-muted mt-2">
									These acquired/lost/stolen counters are optional inventory reference fields and can be left blank if you do not
									track them here.
								</div>
							</div>
							{this.state.partError && <p style={modalStyles.errorText}>{this.state.partError}</p>}
							<div style={modalStyles.buttonGroup}>
								<button
									style={modalStyles.cancelButton}
									onClick={this.handleClosePartModal}
									disabled={this.state.loading}
								>
									Cancel
								</button>
								<button
									style={modalStyles.saveButton}
									onClick={this.handleSavePart}
									disabled={this.state.loading}
								>
									{this.state.loading ? <i className="fa fa-spinner fa-spin"/> : null}
									{' '}Save
								</button>
							</div>
						</div>
					</div>
				)}
			</div>
		);
	}
}

const stationPageStyles = {
	heroBand: {
		background: 'linear-gradient(135deg, #eef8ff 0%, #fbfdff 44%, #e6f5f8 100%)',
		border: '1px solid #d6e2f0',
		borderTop: '4px solid #247a9f',
		borderRadius: '14px',
		padding: '22px 24px',
		color: '#17324d',
		boxShadow: '0 10px 24px rgba(12, 55, 92, 0.08)',
	},
	heroEyebrow: {
		display: 'inline-flex',
		alignItems: 'center',
		padding: '4px 10px',
		borderRadius: '999px',
		backgroundColor: '#dff2f7',
		color: '#1f718f',
		fontSize: '11px',
		textTransform: 'uppercase',
		letterSpacing: '0.08em',
		fontWeight: 700,
	},
	heroTitle: {
		fontSize: '27px',
		lineHeight: 1.2,
		margin: '8px 0 10px',
		fontWeight: 700,
	},
	heroSubtext: {
		fontSize: '14px',
		color: '#557086',
		maxWidth: '520px',
	},
	heroStatsWrap: {
		background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.96) 0%, #edf9fb 100%)',
		border: '1px solid #d7e4f2',
		borderRadius: '12px',
		padding: '12px 14px',
		boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.78)',
	},
	heroSelect: {
		backgroundColor: '#ffffff',
		border: '1px solid #c9d9e8',
		color: '#17324d',
	},
	heroStatRow: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		fontSize: '13px',
		padding: '7px 0',
		borderBottom: '1px solid #e3ecf5',
		gap: '8px',
	},
	heroStatLabel: {
		color: '#5f7489',
	},
	heroStatValue: {
		color: '#17324d',
		fontSize: '15px',
		fontWeight: 700,
	},
	cardShell: {
		border: '1px solid #d6e2f0',
		borderRadius: '12px',
		boxShadow: '0 8px 18px rgba(12, 55, 92, 0.09)',
		overflow: 'hidden',
	},
	cardHeader: {
		background: 'linear-gradient(90deg, #f8fbff 0%, #edf4ff 100%)',
		borderBottom: '1px solid #d7e4f4',
	},
	primaryAction: {
		boxShadow: '0 8px 16px rgba(16, 93, 148, 0.2)',
	},
	secondaryAction: {
		borderColor: '#9fb7d4',
		color: '#2c5278',
		backgroundColor: '#f6faff',
	},
	infoAction: {
		boxShadow: '0 6px 14px rgba(18, 120, 162, 0.18)',
	},
	dangerAction: {
		boxShadow: '0 6px 14px rgba(160, 47, 33, 0.18)',
	},
	table: {
		marginBottom: 0,
	},
	expandToggle: {
		textDecoration: 'none',
		color: '#1369a9',
	},
	itemCountBadge: {
		marginLeft: '8px',
		fontSize: '11px',
		fontWeight: 700,
		textTransform: 'uppercase',
		letterSpacing: '0.05em',
		backgroundColor: '#e8f3ff',
		color: '#1d5789',
		padding: '2px 8px',
		borderRadius: '999px',
		border: '1px solid #cde2f8',
	},
	lowStockCountBadge: {
		fontSize: '11px',
		fontWeight: 700,
		textTransform: 'uppercase',
		letterSpacing: '0.05em',
		backgroundColor: '#fff0d9',
		color: '#9a4f00',
		padding: '2px 8px',
		borderRadius: '999px',
		border: '1px solid #f2cf99',
	},
	expandedCell: {
		padding: 0,
		backgroundColor: '#f6f9fd',
	},
	expandedBody: {
		padding: '15px',
	},
	partsTable: {
		backgroundColor: '#fff',
	},
};

const modalStyles = {
	modalOverlay: {
		position: 'fixed',
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		backgroundColor: 'rgba(0, 0, 0, 0.5)',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		zIndex: 1000,
	},
	modalContent: {
		backgroundColor: 'white',
		padding: '25px',
		borderRadius: '8px',
		width: '500px',
		maxWidth: '90vw',
		maxHeight: '90vh',
		overflowY: 'auto',
		boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
	},
	modalContentWide: {
		backgroundColor: 'white',
		padding: '25px',
		borderRadius: '8px',
		width: '760px',
		maxWidth: '95vw',
		maxHeight: '90vh',
		overflowY: 'auto',
		boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
	},
	heading: {
		fontSize: '24px',
		fontWeight: '600',
		marginTop: 0,
		marginBottom: '20px',
		color: '#333',
		borderBottom: '1px solid #eee',
		paddingBottom: '10px',
	},
	inputGroup: {
		marginBottom: '20px',
	},
	label: {
		display: 'block',
		marginBottom: '8px',
		fontWeight: '500',
		color: '#555',
		fontSize: '16px',
	},
	inputField: {
		width: '100%',
		padding: '10px 12px',
		border: '1px solid #ddd',
		borderRadius: '4px',
		fontSize: '16px',
		boxSizing: 'border-box',
		transition: 'border-color 0.2s ease',
		outline: 'none',
	},
	buttonGroup: {
		display: 'flex',
		justifyContent: 'flex-end',
		gap: '12px',
	},
	cancelButton: {
		padding: '10px 18px',
		backgroundColor: '#f1f1f1',
		color: '#333',
		border: 'none',
		borderRadius: '4px',
		cursor: 'pointer',
		fontWeight: '500',
		transition: 'background-color 0.2s ease',
	},
	saveButton: {
		padding: '10px 18px',
		backgroundColor: '#007bff',
		color: 'white',
		border: 'none',
		borderRadius: '4px',
		cursor: 'pointer',
		fontWeight: '500',
		transition: 'background-color 0.2s ease',
	},
	errorText: {
		color: '#dc3545',
		margin: '8px 0 0 0',
		fontSize: '14px',
		fontWeight: '500',
	},
};


export default Framework.Components.withRootState(MrpStation);
